import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, Euro } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useVehicleById } from '../hooks/useVehicles';
import PublicHeader from '../components/PublicHeader';

const VehicleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { vehicle, loading } = useVehicleById(id);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    if (vehicle?.image_url) {
      setSelectedImage(vehicle.image_url);
    }
  }, [vehicle]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Lädt...</div>;
  }

  if (!vehicle) {
    return <div className="min-h-screen flex items-center justify-center">Fahrzeug nicht gefunden</div>;
  }

  const allImages = [vehicle.image_url, ...(vehicle.additional_images || [])].filter(Boolean);

  return (
    <div className="min-h-screen bg-gray-50">
      <PublicHeader title="Fahrzeugdetails" />

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* Bilder */}
          <div>
            {/* Haupt-Bild */}
            <div className="mb-4">
              <img
                src={selectedImage || vehicle.image_url}
                alt={vehicle.name}
                loading="eager"
                className="w-full h-96 object-cover rounded-lg shadow-lg"
              />
            </div>

            {/* Thumbnail-Galerie */}
            {allImages.length > 1 && (
              <div className="grid grid-cols-4 gap-2">
                {allImages.map((img, idx) => (
                  <img
                    key={idx}
                    src={img}
                    alt={`${vehicle.name} ${idx + 1}`}
                    loading="lazy"
                    onClick={() => setSelectedImage(img)}
                    className={`w-full h-24 object-cover rounded cursor-pointer border-2 transition-all ${
                      selectedImage === img ? 'border-[#FDB913] scale-105' : 'border-gray-300 hover:border-[#FDB913]/50'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <span className="inline-block bg-[#FDB913] text-[#1a1a1a] px-3 py-1 rounded-full text-sm font-semibold mb-3">
              {vehicle.category}
            </span>
            <h1 className="text-4xl font-bold text-[#2C3E50] mb-4">{vehicle.name}</h1>
            
            {vehicle.description && (
              <p className="text-gray-700 mb-6 text-lg leading-relaxed">
                {vehicle.description}
              </p>
            )}

            {/* Preise */}
            <div className="bg-white rounded-lg p-6 shadow-md mb-6">
              <h3 className="text-xl font-bold text-[#2C3E50] mb-4 flex items-center">
                <Euro className="mr-2 h-5 w-5" />
                Mietpreise
              </h3>
              <div className="space-y-2">
                {vehicle.daily_price && (
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-gray-600">Pro Tag:</span>
                    <span className="text-2xl font-bold text-[#FDB913]">{vehicle.daily_price} €</span>
                  </div>
                )}
                {vehicle.weekly_price && (
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-gray-600">Pro Woche:</span>
                    <span className="text-2xl font-bold text-[#FDB913]">{vehicle.weekly_price} €</span>
                  </div>
                )}
                {vehicle.monthly_price && (
                  <div className="flex justify-between items-center py-2">
                    <span className="text-gray-600">Pro Monat:</span>
                    <span className="text-2xl font-bold text-[#FDB913]">{vehicle.monthly_price} €</span>
                  </div>
                )}
              </div>
            </div>

            {/* Verfügbarkeit */}
            <div className="bg-white rounded-lg p-6 shadow-md mb-6">
              <h3 className="text-xl font-bold text-[#2C3E50] mb-4 flex items-center">
                <Calendar className="mr-2 h-5 w-5" />
                Verfügbarkeit
              </h3>
              <div className="flex items-center gap-3">
                <div className={`w-4 h-4 rounded-full ${vehicle.available ? 'bg-green-500' : 'bg-red-500'}`}></div>
                <span className="text-lg">
                  {vehicle.available ? (
                    <span className="text-green-600 font-semibold">
                      Verfügbar ({vehicle.quantity || 1} Einheit{vehicle.quantity !== 1 ? 'en' : ''})
                    </span>
                  ) : (
                    <span className="text-red-600 font-semibold">Derzeit nicht verfügbar</span>
                  )}
                </span>
              </div>
            </div>

            {/* Ausstattung */}
            {vehicle.features && vehicle.features.length > 0 && (
              <div className="bg-white rounded-lg p-6 shadow-md mb-6">
                <h3 className="text-xl font-bold text-[#2C3E50] mb-4">Ausstattung</h3>
                <div className="flex flex-wrap gap-2">
                  {vehicle.features.map((feature, idx) => (
                    <span key={idx} className="bg-[#FDB913]/20 text-[#2C3E50] px-3 py-1 rounded-full text-sm">
                      ✓ {feature}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* CTA Button */}
            <Button
              size="lg"
              className="w-full bg-[#FDB913] hover:bg-[#F59E0B] text-[#1a1a1a] font-bold py-6 text-lg"
              onClick={() => {
                navigate('/#kontakt', { state: { vehicle: vehicle.name } });
              }}
            >
              Jetzt anfragen
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleDetail;
